import { ChevronUpIcon } from '@fluentui/react-icons-mdl2';
import {
  Button,
  ChevronDownIcon,
  Datepicker,
  Dropdown,
  Flex,
  ProviderConsumer as FluentUIThemeConsumer,
  Input,
  Popup,
  Text,
  TrashCanIcon,
} from '@fluentui/react-northstar';
import update from 'immutability-helper';
import { DateTime } from 'luxon';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatepickerLocalizations } from '../../App';
import AppCSS from '../../App.module.css';
import {
  appState,
  AppStateContext,
  AppStateValue,
  FilterDropdownProps,
  useSetState
} from '../../AppState';
import MgtCss from '../../mgt.module.css';
import { formatTimeInput } from '../../templates/CustomComponents/Utils/CustomElementUtils';
import { ListContext } from '../../tikit/List/ListContextProvider';
import { getStatusIdByGuid, TableFilterOptions } from '../../tikit/ticketHelper';
import { setChildFiltersInStorage } from '../../tikit/toolbar/CustomViewPopup/heplers/customPopupHelper';
import ToolbarCSS from '../../tikit/toolbar/Toolbar.module.css';
import { searchUsers } from '../cache/PlatformUserCache';
import {
  ConvertDateToLocale,
  IsDateFuture,
  IsDateLate,
  IsDateNextWeek,
  IsDateThisWeek,
  IsDateToday,
  IsDateTomorrow
} from '../common/ConvertDate';
import { ThemeColorScheme } from '../common/TeamsTheme';
import { Ticket } from '../interfaces/ticket.interface';
import { platformService } from '../services/platform.service';
import { CustomProperty_TimeInputWrapperClass, FilterDataType, FILTERS, LICENSE_FEATURE, MeTokenId, ScopeTokens, STATUS_GUID, TICKET_PAGINATION } from '../utils/constants';
import { CheckFeature } from './CheckFeature';
import { filterTicketsByCustomProperties, filterTicketsByScalarNumbers } from './customPropertyTicketFilter';
import './Filter.css';
import FilterCSS from './Filter.module.css';
import { NestedFilter } from './NestedFilter/NestedFilter';
import { getCardTimeInputISOFormatted } from './TicketTemplateAnswer';
import { themeNames } from '@fluentui/react-teams';
import { CustomTimePicker } from '../../custom-properties/CustomTimePicker';
import { TikitPeoplePicker } from './PeoplePicker/TikitPeoplePicker';

export interface FilterPropsItems {
  id: number;
  title: string;
  key: string;
  values: FilterAccordionValues[];
  stringValue?: string;
  FilterDataType?: 'boolean' | 'string' | 'number' | 'date' | 'time' | 'any';
  sortOrder:number;
}

interface FilterProps {
items: FilterPropsItems[];
open?: boolean;
onChange?: (interaction: any) => void;
page?: string;
defaultFilters?: FilterPropsItems[];
}

export interface FilterAccordionValues {
  key: number;
  title: string;
  selected: boolean;
  position?: number | null;
  parentId?: number | null;
  aadObjectId?: string | null;
  upn?: string | null;
  date?: string | null;
  number?: string | null;
}

export interface FilterAccordionProps {
id: number | string;
clearNonce?: number;
title: string;
values: FilterAccordionValues[];
onInteraction?: (interaction: any) => void;
onRemoveFromView?: (id: number) => void;
sortOrder: number;
selected?: FilterPropsItems[];
isCollapsed: boolean;
stringValue?: string;
FilterDataType?: 'boolean' | 'string' | 'number' | 'date' | 'time' | 'any';
filterState?: any;
}

interface TreeItemProps {
id: string;
value: string;
onSelectCallback: () => void;
isSelected?: boolean;
paddingLeft: string;
}

const TOOLBAR_ACTIVE_FILTERS_STORAGE_KEY = 'toolbar_active_filters';
const KNOWN_FILTER_IDS = new Set(
  Object.values(FILTERS)
    .map((filter) => filter?.id)
    .filter((id): id is number => typeof id === 'number')
);

const isCustomPropertyFilterId = (id: number) => !KNOWN_FILTER_IDS.has(id);

const getStoredVisibleFilterIds = () => {
  const storedValue = localStorage.getItem(TOOLBAR_ACTIVE_FILTERS_STORAGE_KEY);
  if (!storedValue) {
    return [];
  }

  const parsedValue = JSON.parse(storedValue);
  if (!Array.isArray(parsedValue)) {
    return [];
  }

  return parsedValue
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id));
};
const emptySearchDropdowns = () => ({ [FILTERS.ASSIGNEE.title]: '', [FILTERS.REQUESTERS.title]: '', [FILTERS.AffectedUsers.title]: '', [FILTERS.TicketCollaborators.title]: '' });

export const FilterAccordion = (props: FilterAccordionProps) => {
  const api = new platformService();
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [selected, setSelected] = useState<FilterAccordionValues[]>([]);
  const [searchDropdowns, setSearchDropdowns] = useState(emptySearchDropdowns());
  const clearNonceRef = useRef(props.clearNonce);
  const isMounted = useRef({ [FILTERS.ASSIGNEE.title]: false, [FILTERS.REQUESTERS.title]: false, [FILTERS.AffectedUsers.title]: false, [FILTERS.TicketCollaborators.title]: false });
  const searchFieldSelected = useRef({ [FILTERS.ASSIGNEE.title]: false, [FILTERS.REQUESTERS.title]: false, [FILTERS.AffectedUsers.title]: false, [FILTERS.TicketCollaborators.title]: false, });
  const { t } = useTranslation();

const isSearchFieldActive = [FILTERS.ASSIGNEE.title, FILTERS.REQUESTERS.title, FILTERS.AffectedUsers.title, FILTERS.TicketCollaborators.title].includes(props.title);
  const isModifiedDate = [FILTERS.MODIFIED_DATE.title].includes(props.title);
  const isCreatedDate = [FILTERS.CREATED_DATE.title].includes(props.title);
  const isCustomPropertyDate = props.FilterDataType == FilterDataType.Date;
  const isCustomPropertyTime = props.FilterDataType == FilterDataType.Time;
  const isNumberFilter = props.FilterDataType == FilterDataType.Number;
  let createdDate = props?.selected?.find(filter => filter.id === FILTERS.CREATED_DATE.id)?.values?.find(value => value.selected)?.date;
  let modifiedDate = props?.selected?.find(filter => filter.id === FILTERS.MODIFIED_DATE.id)?.values?.find(value => value.selected)?.date;
  // Matched on this accordion's own id. Finding by data type returns the FIRST filter of that
  // type, which was harmless only while a tenant had at most one - two number properties, or one
  // plus Total Agent Time, and both accordions loaded and cleared the same value.
  let customDate = props?.selected?.find(filter => filter.id === props.id)?.values?.find(value => value.selected)?.date;
  let customTime = props?.selected?.find(filter => filter.id === props.id)?.values?.find(value => value.selected)?.date;
  let customNumber = props?.selected?.find(filter => filter.id === props.id)?.values?.find(value => value.selected)?.number;
  const [selectedModifiedDate, setSelectedModifiedDate] = useState(modifiedDate ? new Date(modifiedDate) : null);
  const [selectedCreatedDate, setSelectedCreatedDate] = useState(createdDate ? new Date(createdDate) : null);
  const [selectedCustomDate, setSelectedCustomDate] = useState(customDate ? new Date(customDate) : null);
  const [selectedCustomTime, setSelectedCustomTime] = useState(customTime);
  const [selectedCustomNumber, setSelectedCustomNumber] = useState(customNumber);
  const filterId = Number(props.id);
  const isRequesterField = filterId === FILTERS.REQUESTERS.id;
  const isAffectedUsersField = filterId === FILTERS.AffectedUsers.id;
  const [requesterValues, setRequesterValues] = useState<PlatformUser[]>([]);
  const [affectedUsersValues, setAffectedUsersValues] = useState<PlatformUser[]>([]);

  function getSelectedFilterValues() {
    if (isSearchFieldActive) {
      return props.selected.find(filter => filter.id === props.id)?.values
    }
    return selected;
  }

// getSelectedFilterValues
const pushSelected = async (id: number) => {
  const selectedFilterValues = getSelectedFilterValues();

  const findFilterValue = selectedFilterValues.findIndex(obj => obj.key === id);
  let getCurrentValues = selectedFilterValues;
  if (findFilterValue >= 0) {
    let updateValues = update(getCurrentValues[findFilterValue], {
      selected: { $set: !getCurrentValues[findFilterValue].selected }
    });
    getCurrentValues = update(getCurrentValues, {
      $splice: [[findFilterValue, 1, updateValues]]
    });
  } else if (id === MeTokenId) {
    getCurrentValues = update(getCurrentValues, { $push: [{ key: ScopeTokens.MeTokenPlatformUser.Id, title: ScopeTokens.MeTokenPlatformUser.FullName, aadObjectId: ScopeTokens.MeTokenPlatformUser.AadObjectId, upn: ScopeTokens.MeTokenPlatformUser.UserName, selected: true }] });
  } else {
    const { data } = await api.getPlatformUser(id);
    getCurrentValues = update(getCurrentValues, { $push: [{ key: data.Id, title: data.FullName, aadObjectId: data.AadObjectId, upn: data.UserName, selected: true }] });
  }

  if (isSearchFieldActive) {
    const currentDropdownValues = getSearchedValues(getCurrentValues, searchDropdowns[props.title]);
    setSelected(currentDropdownValues);
  } else {
    setSelected(getCurrentValues);
  }

  if (props.onInteraction) {
    props.onInteraction({
      event: 'setFilter',
      data: { id: Number(props.id), sortOrder: props.sortOrder, values: getCurrentValues }
    });
  }
};

const countSelected = () => {
  const selectedFilterValues = getSelectedFilterValues();
  return selectedFilterValues.filter(s => s.selected).length;
};

const setAccordionStateCookie = (isCollapsed: boolean) => {
  if (props.onInteraction) {
    props.onInteraction({
      event: 'setAccordion',
      data: { id: props.id, sortOrder: props.sortOrder, isCollapsed: isCollapsed }
    });
  }
};

const onChangeSearchDropdown = function(e: any) {
  const value: string = e.target.value;
  setSearchDropdowns({ ...searchDropdowns, [props.title]: value });
  let findDefaultSelectedValues = props.selected.find((s) => s.id === props.id);
  const dropdownValues = getSearchedValues(findDefaultSelectedValues.values, value);
  setSelected(dropdownValues);
}

const setPeoplePickerData = async (filters: FilterAccordionValues[]) => {
  const users = await Promise.all(
    (filters ?? []).map((el) =>
      searchUsers({ Email: el.upn, UserName: el.upn, AadObjectId: el.aadObjectId } as PlatformUser)
    )
  );

  const userList = users.filter(Boolean) as PlatformUser[];
  if (isRequesterField) {
    setRequesterValues(userList);
  }
  if (isAffectedUsersField) {
    setAffectedUsersValues(userList);
  }
};

useEffect(() => {
  const cleared = props.clearNonce !== clearNonceRef.current;
  clearNonceRef.current = props.clearNonce;
  if (cleared) {
    setSearchDropdowns(emptySearchDropdowns());
    setSelectedCustomNumber('');
    setSelectedCustomTime(null);
    setSelectedCustomDate(null);
    setSelectedCreatedDate(null);
    setSelectedModifiedDate(null);
  }

  setIsHidden(props.isCollapsed);
}, [props.isCollapsed]);

useEffect(() => {
  let findDefaultSelectedValues = props.selected.find(s => s.id === props.id);
  if (findDefaultSelectedValues) {
    if (!isSearchFieldActive) {
      setSelected(findDefaultSelectedValues.values);
    }
    if (isSearchFieldActive) {
      if (!isMounted.current[findDefaultSelectedValues.title]) {
        const dropdownValues = getSearchedValues(findDefaultSelectedValues.values, '');
        setSelected(dropdownValues);
        isMounted.current[findDefaultSelectedValues.title] = true;
      }
      if (searchFieldSelected.current[findDefaultSelectedValues.title]) {
        const dropdownValues = getSearchedValues(findDefaultSelectedValues.values, '');
        setSelected(dropdownValues);
        searchFieldSelected.current[findDefaultSelectedValues.title] = false;
      }
      if (!searchFieldSelected.current[findDefaultSelectedValues.title] && countSelected() == 0) {
        const cleared = props.clearNonce !== clearNonceRef.current;
        const searchText = cleared ? '' : searchDropdowns[props.title];
        const dropdownValues = getSearchedValues(findDefaultSelectedValues.values, searchText);
        setSelected(dropdownValues);
      }

      if (isRequesterField || isAffectedUsersField) {
        setPeoplePickerData(findDefaultSelectedValues?.values?.filter((s) => s.selected === true) ?? []);
      }
    }
  }
}, [props.selected]);

const setRequesterFilter = async (theseUsers: PlatformUser[]) => {
  const sourceValues = getSelectedFilterValues() || [];
  const nextValues = sourceValues.map((item) => ({ ...item, selected: false }));

  const upsertUser = async (user: PlatformUser) => {
    const matchedIndex = nextValues.findIndex((item) =>
      item.title === user.FullName ||
      (!!item.aadObjectId && item.aadObjectId === user.AadObjectId) ||
      item.upn === user.UserName
    );

    if (matchedIndex >= 0) {
      nextValues[matchedIndex].selected = true;
      return;
    }

    const resolvedUser = await searchUsers({
      Email: user.Email ?? user.UserName,
      GivenName: user.GivenName,
      FamilyName: user.FamilyName,
      UserName: user.UserName,
      AadObjectId: user.AadObjectId
    } as PlatformUser);

    if (!resolvedUser) {
      return;
    }

    nextValues.push({
      key: resolvedUser.Id,
      title: resolvedUser.FullName,
      aadObjectId: resolvedUser.AadObjectId,
      upn: resolvedUser.UserName,
      selected: true
    });
  };

  for (const user of theseUsers) {
    await upsertUser(user);
  }

  setSelected(nextValues);
  if (isRequesterField) {
    setRequesterValues(theseUsers);
  }
  if (isAffectedUsersField) {
    setAffectedUsersValues(theseUsers);
  }

  if (props.onInteraction) {
    props.onInteraction({
      event: 'setFilter',
      data: { id: Number(props.id), sortOrder: props.sortOrder, values: nextValues }
    });
  }
};

  const customFormatter = date => {
    return ConvertDateToLocale(date, DateTime.DATE_SHORT);
  };
  
  const limitSelectionOnModifiedDateToOneOperator = (item: any) => {
    const modifiedDateOperatorIsAlreadySelected = props?.selected?.find(x => x.id == FILTERS.MODIFIED_DATE.id).values?.find(x => x.selected);
    if (modifiedDateOperatorIsAlreadySelected == null || modifiedDateOperatorIsAlreadySelected == undefined) {
      setSelectedModifiedDate(null);
      pushSelected(item.key);
      return;
    }
    else {
      //unsetting previously selected value and attaching selected date with newly selected operator
      let updatedList = selected.map(listValue => {
        if (listValue.selected && listValue.key == modifiedDateOperatorIsAlreadySelected.key && listValue.title == modifiedDateOperatorIsAlreadySelected.title) {
          listValue.selected = false;
          listValue.date = null;
        }
        if (item.key == listValue.key && item.key != modifiedDateOperatorIsAlreadySelected.key){
          listValue.date = selectedModifiedDate?.toString();
        }
        return listValue;
      });

      updatedList = JSON.parse(JSON.stringify(updatedList))
      setSelected(updatedList);
      
      //pushing the newly selected value
      if (item.key != modifiedDateOperatorIsAlreadySelected?.key) {
          pushSelected(item.key);
      }
      else {
        setSelectedModifiedDate(null);
      }
      return;
    }
  }

  const limitSelectionOnCreatedDateToOneOperator = (item: any) => {
    var createdDateOperatorIsAlreadySelected = props?.selected?.find(x => x.id == FILTERS.CREATED_DATE.id).values?.find(x => x.selected);
    if (createdDateOperatorIsAlreadySelected == null || createdDateOperatorIsAlreadySelected == undefined) {
      setSelectedCreatedDate(null);
      pushSelected(item.key);
      return;
    }
    else {
      //unsetting previously selected value and attaching selected date with newly selected operator
      let updatedList = selected.map(listValue => {
        if (listValue.selected && listValue.key == createdDateOperatorIsAlreadySelected.key && listValue.title == createdDateOperatorIsAlreadySelected.title) {
          listValue.selected = false;
          listValue.date = null;
        }
        if (item.key == listValue.key && item.key != createdDateOperatorIsAlreadySelected.key){
          listValue.date = selectedCreatedDate?.toString();
        }
        return listValue;
      });

      updatedList = JSON.parse(JSON.stringify(updatedList))
      setSelected(updatedList);
      
      //pushing the newly selected value
      if (item.key != createdDateOperatorIsAlreadySelected?.key) {
          pushSelected(item.key);
      }
      else {
        setSelectedCreatedDate(null);
      }
      return;
    }
  }

  const limitSelectionOnCustomDateToOneOperator = (item: any) => {
    const customDateOperatorIsAlreadySelected = props?.selected?.find(x => x.id === props.id)?.values?.find(x => x.selected);
    if (customDateOperatorIsAlreadySelected == null || customDateOperatorIsAlreadySelected == undefined) {
      setSelectedCustomDate(null);
      pushSelected(item.key);
    }
    else {
      //unsetting previously selected value and attaching selected date with newly selected operator
      let updatedList = selected.map(listValue => {
        if (listValue.selected && listValue.key == customDateOperatorIsAlreadySelected.key && listValue.title == customDateOperatorIsAlreadySelected.title) {
          listValue.selected = false;
          listValue.date = null;
        }
        if (item.key == listValue.key && item.key != customDateOperatorIsAlreadySelected.key){
          listValue.date = selectedCustomDate?.toString();
        }
        return listValue;
      });

      updatedList = JSON.parse(JSON.stringify(updatedList))
      setSelected(updatedList);
      
      //pushing the newly selected value
      if (item.key != customDateOperatorIsAlreadySelected?.key) {
          pushSelected(item.key);
      }
      else {
        setSelectedCustomDate(null);
      }
    }
  }

  const limitSelectionOnCustomValueToOneOperator = (item: any, selectedValue: string, setValue: React.Dispatch<React.SetStateAction<string>>, propKey: string) => {
    const customValueOperatorIsAlreadySelected = props?.selected?.find(x => x.id === props.id)?.values?.find(x => x.selected);
    if (customValueOperatorIsAlreadySelected == null || customValueOperatorIsAlreadySelected == undefined) {
      setValue(null);
      pushSelected(item.key);
    }
    else {
      //unsetting previously selected value and attaching selected date with newly selected operator
      let updatedList = selected.map(listValue => {
        if (listValue.selected && listValue.key == customValueOperatorIsAlreadySelected.key && listValue.title == customValueOperatorIsAlreadySelected.title) {
          listValue.selected = false;
          listValue[propKey] = null;
        }
        if (item.key == listValue.key && item.key != customValueOperatorIsAlreadySelected.key){
          listValue[propKey] = selectedValue;
        }
        return listValue;
      });

      updatedList = JSON.parse(JSON.stringify(updatedList))
      setSelected(updatedList);
      
      //pushing the newly selected value
      if (item.key != customValueOperatorIsAlreadySelected?.key) {
          pushSelected(item.key);
      }
      else {
        setValue(null);
      }
    }
  }

  const onDateChange = (p: any, setDate: React.Dispatch<React.SetStateAction<Date>>) => {
    let selectedDate = p.value === null ? null : p.value.toISOString();
    let selectedOperator = selected?.find(val => val.selected);
    if (selectedOperator) {
      let updatedList = selected.map(listValue => {
        if (listValue.selected) {
          listValue.date = selectedDate;
        }
        return listValue;
      });
      
      updatedList = JSON.parse(JSON.stringify(updatedList))
      setSelected(updatedList);
      if (props.onInteraction) {
        props.onInteraction({
          event: 'setFilter',
          data: { id: Number(props.id), sortOrder: props.sortOrder, values: updatedList }
        });
      }
    }

    setDate(new Date(selectedDate));
  };

  const onValueChange = (selectedValue: string, setValue: React.Dispatch<React.SetStateAction<string>>, propKey: string) => {
    let selectedOperator = selected?.find(val => val.selected);
    if (selectedOperator) {
      let updatedList = selected.map(listValue => {
        if (listValue.selected) {
          listValue[propKey] = selectedValue;
        }
        return listValue;
      });
      
      updatedList = JSON.parse(JSON.stringify(updatedList))
      setSelected(updatedList);
      if (props.onInteraction) {
        props.onInteraction({
          event: 'setFilter',
          data: { id: Number(props.id), sortOrder: props.sortOrder, values: updatedList }
        });
      }
    }

    setValue(selectedValue);
  };

  const getAfterNameDisplay = () => {
    switch(props?.FilterDataType)
    {
      case FilterDataType.Boolean:
        let selectedBool = getSelectedFilterValues().find(i=>i.selected);
        return selectedBool ? '(1)' : "(0)";
      case FilterDataType.String:
        return `(${props?.stringValue ? "1" : "0"})`;
      default:
        return `(${countSelected()})`;
    }
  }

  return (
    <FluentUIThemeConsumer
      key={`filter-field-${props.title}`}
      render={globalTheme => (
          <li key={props.sortOrder} style={{display: props.id==FILTERS.Title.id ? "none": "block",  borderTop: `2px solid ${globalTheme.siteVariables.colorScheme.default.background}`, ...ThemeColorScheme(globalTheme.siteVariables) }} >
            <div
              onClick={() => {
                if (isSearchFieldActive) {
                  searchFieldSelected.current[props.title] = true;
                }
                setAccordionStateCookie(!isHidden);
                setIsHidden(!isHidden);
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (isSearchFieldActive) {
                    searchFieldSelected.current[props.title] = true;
                  }
                  setAccordionStateCookie(!isHidden);
                  setIsHidden(!isHidden);
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={!isHidden}
            >
              <div style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }} className={`flex justify-between px-3 py-2`} >
                <Text content={
                  `${props.title} ${getAfterNameDisplay()}`
                  } weight="semibold" />
                <div className='flex items-center gap-2'>
                  {isHidden ? <ChevronDownIcon /> : <ChevronUpIcon /> }
                  {props.onRemoveFromView && (
                    <span
                      className='cursor-pointer'
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onRemoveFromView(Number(props.id));
                      }}
                      title='Remove filter from view'
                      aria-label='Remove filter from view'
                    >
                      <TrashCanIcon size='small' outline />
                    </span>
                  )}
                </div>
              </div>
            </div>

          <ul className={`${isHidden && 'hidden'} ${isSearchFieldActive && FilterCSS.scrollList}`} style={{ borderTop: `1px solid ${globalTheme.siteVariables.colorScheme.default.border3}` }}>
            {(isSearchFieldActive && !isRequesterField && !isAffectedUsersField) &&
              <Input
                fluid
                value={searchDropdowns[props.title] ?? ''}
                style={{
                  backgroundColor: globalTheme.siteVariables.colorScheme.default.background1,
                  padding: '2px 0px',
                  marginTop: "2px",
                }}
                onChange={onChangeSearchDropdown}
                inverted
                placeholder={t('ticket.ticket-list.configure-column.filter-by-assignee')}
              />
            }
            {isRequesterField || isAffectedUsersField
            ? <TikitPeoplePicker options={props.filterState?.usersInRole ?? []}
              isMultiple
              value={isRequesterField ? requesterValues : affectedUsersValues}
              onChange={async (selectedUsers) => await setRequesterFilter(selectedUsers)}
              globalTheme={globalTheme}
              menuPortalBehavior='body'
              enableMeToken={true}
            /> 
            : 
              <>
                {props.FilterDataType == FilterDataType.String &&
                  <Input
                  fluid
                  className={ToolbarCSS.filterSearch}
                  style={{
                    backgroundColor: globalTheme.siteVariables.colorScheme.default.background1,
                    padding: '2px 0px', marginTop: "2px",
                  }}
                  value={props?.stringValue ?? ''}
                  onChange={(_,e)=>{
                    if (props.onInteraction) {
                      props.onInteraction({
                        event: 'setFilter',
                        data: { id: props.id, sortOrder: props.sortOrder, stringValue: e.value, values:[] }
                      });
                    }
                  }}
                  inverted
                  placeholder={`${t('ticket.child-filter.title')}...`}
                />
                }
                
                {props.FilterDataType != FilterDataType.String &&
                  <NestedFilter filterItems={selected} onClick={(item: any) => {
                    
                    if (props?.title == FILTERS.MODIFIED_DATE.title) {
                      limitSelectionOnModifiedDateToOneOperator(item);
                    }
                    else if (props?.title == FILTERS.CREATED_DATE.title) {
                      limitSelectionOnCreatedDateToOneOperator(item);
                    }
                    else if (props?.FilterDataType == FilterDataType.Date) {
                      limitSelectionOnCustomDateToOneOperator(item);
                    }
                    else if (props?.FilterDataType == FilterDataType.Time) {
                      limitSelectionOnCustomValueToOneOperator(item, selectedCustomTime, setSelectedCustomTime, "date");
                    }
                    else if (props?.FilterDataType == FilterDataType.Number) {
                      limitSelectionOnCustomValueToOneOperator(item, selectedCustomNumber, setSelectedCustomNumber, "number");
                    }
                    else {

                      if (props?.FilterDataType==FilterDataType.Boolean) {
                        selected.find(i=>i.key != item.key).selected=false;
                      }

                      pushSelected(item.key);
                    }
                  }} />
                }
                
                <div className={`${selected?.find(val => val.selected) ? '' : 'disabledDatePicker'}`}>
                  {isModifiedDate && (<Datepicker
                    {...DatepickerLocalizations}
                    disabled={!selected.some(val => val.selected)}
                    firstDayOfWeek={0}
                    formatMonthDayYear={customFormatter}
                    className={`${MgtCss.fluidDate} ${MgtCss.inverted}`}
                    onDateChange={(_, p) => { onDateChange(p, setSelectedModifiedDate) }}
                    selectedDate={selected?.find(val => val.selected) ? selectedModifiedDate : null}
                  />)}
                  {isCreatedDate && (<Datepicker
                    {...DatepickerLocalizations}
                    disabled={!selected.some(val => val.selected)}
                    firstDayOfWeek={0}
                    formatMonthDayYear={customFormatter}
                    className={`${MgtCss.fluidDate} ${MgtCss.inverted}`}
                    onDateChange={(_, p) => { onDateChange(p, setSelectedCreatedDate) }}
                    selectedDate={selected?.find(val => val.selected) ? selectedCreatedDate : null}
                  />)}
                  {isCustomPropertyDate && (<Datepicker
                    {...DatepickerLocalizations}
                    disabled={!selected.some(val => val.selected)}
                    firstDayOfWeek={0}
                    formatMonthDayYear={customFormatter}
                    className={`${MgtCss.fluidDate} ${MgtCss.inverted}`}
                    onDateChange={(_, p) => { onDateChange(p, setSelectedCustomDate) }}
                    selectedDate={selected?.find(val => val.selected) ? selectedCustomDate : null}
                  />)}
                  {isNumberFilter && ( <Input
                    type="number"
                    fluid
                    disabled={!selected.some(val => val.selected)}
                    min={0}
                    value={selectedCustomNumber ?? ''}
                    onChange={(_, p) => {
                        onValueChange(p.value, setSelectedCustomNumber, "number")
                    }}
                    placeholder={props.id == FILTERS.TOTAL_AGENT_TIME.id
                      ? t('ticket.child-filter.total-agent-time-placeholder')
                      : t('ticket.child-filter.type-a-number')}
                  />)} 
                </div>
                {isCustomPropertyTime && (
                <Flex className={CustomProperty_TimeInputWrapperClass(globalTheme.siteVariables.theme === themeNames.Dark )} >
                  <CustomTimePicker
                      disabled={!selected.some(val => val.selected)}
                      value={selectedCustomTime ? formatTimeInput(selectedCustomTime) : ""}
                      onChange={p => {
                          const timeInput = (p.target as HTMLInputElement).value;
                          let selectedTime = timeInput === null ? null : getCardTimeInputISOFormatted(timeInput);
                          onValueChange(selectedTime, setSelectedCustomTime, "date")
                      }}
                    />
                  </Flex>)} 
              </>
            }
            </ul>
          </li>
        )}
    />
  );
};

export function getSearchedValues(values: FilterAccordionValues[], value: string) {
const selectedValues = [];
const searchedValues = values.filter(
  (selectedValue) => {
    if (selectedValue.selected) {
      selectedValues.push(selectedValue);
      return false;
    }
    return selectedValue.title.toLowerCase().includes(value.toLowerCase());
  }
);
return [...selectedValues, ...searchedValues];
}

interface GetSelectedValuesProps {
filterState: FilterDropdownProps;
filterIndex: number;
}
const getSelectedValues = ({ filterState, filterIndex }: GetSelectedValuesProps) => {
  return filterState.selectedValues.filter(o => o.id === filterIndex )?.[0]?.values?.filter(o => o.selected) ?? []
}
const getKeysOfSelectedFilter = (selectedFilter: FilterAccordionValues[], currentUserId?: number) => {
  // the "Me" scope token is stored as ScopeTokens.MeTokenPlatformUser.Id (-1) and must be
  // resolved to the actual signed-in user's id before it can be matched against ticket data.
  return selectedFilter.map(o => (o.key === ScopeTokens.MeTokenPlatformUser.Id && currentUserId != null) ? currentUserId : o.key) ?? [];
}

const filterTicketsByLifecycle = (tickets: Ticket[], selectedlifeCycles: FilterAccordionValues[]) => {
  if ((selectedlifeCycles?.length ?? 0) == 0) return tickets;
  const filterLifecycleIds: number[] = getKeysOfSelectedFilter(selectedlifeCycles);
  const filteredTickets = tickets?.filter(ticket =>
    (filterLifecycleIds?.includes(0) && ticket.TicketLifecycle == null) || filterLifecycleIds?.includes(ticket.TicketLifecycle?.LifecycleId)
  );
  return [...(filteredTickets ?? [])];
}

const filterTicketsByTags = (tickets: Ticket[], selectedTags: FilterAccordionValues[]) => {
  if ((selectedTags?.length ?? 0) == 0) return tickets;
  const filterTagIds: number[] = getKeysOfSelectedFilter(selectedTags);
  const filteredTickets = tickets?.filter(ticket => ticket.Tags?.some((tag: any) => filterTagIds.includes(tag.Id)));
  return [...(filteredTickets ?? [])];
}

const getFilteredTicketsBySelectedFilters = (filteredTickets: Ticket[], selectedFilters: [FilterAccordionValues[], string, boolean][], currentUserId?: number): Ticket[] => {
  selectedFilters.forEach(([selectedFilter, filterKeyid, includeNullValue]) => {
    if (selectedFilter.length <= 0) { return };

      let selectedFilterIds: number[] = getKeysOfSelectedFilter(selectedFilter, currentUserId);
      if (includeNullValue) {
        const filteredticketsBySelectedFilterId = filteredTickets?.filter(
          (ticket) => (selectedFilterIds?.includes(0) && ticket[filterKeyid] === null) || selectedFilterIds?.includes(ticket[filterKeyid])
        );
        filteredTickets = [...(filteredticketsBySelectedFilterId ?? [])];
      } else {
        filteredTickets = [...(filteredTickets?.filter((ticket) => selectedFilterIds?.includes(ticket[filterKeyid])) ?? [])];
      }
  });

  return filteredTickets
};
const filterTicketsByUserList = (filteredTickets: Ticket[], selectedFilters: [FilterAccordionValues[], string][], currentUserId?: number): Ticket[] => {
  selectedFilters.forEach(([selectedFilter, filterKeyId]) => {
    if (selectedFilter.length <= 0) { return; }
    const selectedFilterIds: number[] = getKeysOfSelectedFilter(selectedFilter, currentUserId);
    const filteredValues = selectedFilterIds.filter(x => x != null);

    filteredTickets = filteredTickets.filter(t => t[filterKeyId].some(x => filteredValues.includes(x.PlatformUserId)) || (selectedFilterIds.includes(0) && t[filterKeyId].length == 0));
  });

  return filteredTickets;
};

const getFilteredTicketsBySelectedDateFilters = (
filteredTickets: Ticket[], 
selectedFilters: [FilterAccordionValues[], string][]
): Ticket[] => {
selectedFilters.forEach(([selectedFilter, fieldName]) => {
  if (selectedFilter.length > 0) {
    let selectedFilterKeys: number[] = getKeysOfSelectedFilter(selectedFilter);
    filteredTickets = filterByDate(filteredTickets, selectedFilterKeys, fieldName);
  }
});

return filteredTickets;
}

const getFilteredTicketsByDateValue = (filteredTickets: Ticket[], selectedFilters: [FilterAccordionValues[], string][]): Ticket[] => {
  selectedFilters.forEach(([selectedFilter, fieldName]) => {
    if (selectedFilter.length > 0) {
      filteredTickets = filterByDateValue(filteredTickets, selectedFilter, fieldName);
    }
  });
 return filteredTickets;
}

export const customizedFilter = (tickets: Ticket[], filterState: FilterDropdownProps, filtersApplyCount: number, currentUserId?: number) => {
  let filteredTickets: Ticket[] = tickets;

  if (filterState?.selectedValues && filterState.selectedValues?.length > 0 && filtersApplyCount) {
    let [selectedDueDate, selectedStatus, selectedPriorities, selectedCategories, 
      selectedAssignee, selectedTeam, selectedGroup, selectedTicketTypes, 
      selectedTags, selectedResolution, selectedRequester, selectedlifeCycles, selectedCreatedDate, selectedModifiedDate, selectedAffectedUser, selectedCollaborator] 
      = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(filterIndex => getSelectedValues({ filterState, filterIndex }));

    if (filterState.queryValue !== '') {
      filteredTickets = tickets?.filter(t => t.Title?.toLowerCase()?.includes(filterState.queryValue?.toLowerCase()) || t.Id== parseInt(filterState.queryValue) || t.Title?.toLowerCase()?.includes('temp_ticket')) || [];
    }

    filteredTickets = getFilteredTicketsBySelectedDateFilters(filteredTickets, [
      [selectedDueDate, 'DueDate'],
      [selectedResolution, 'ResolutionDate'],
    ])

    filteredTickets = getFilteredTicketsByDateValue(filteredTickets, [
      [selectedCreatedDate, 'CreatedDate'],
      [selectedModifiedDate, 'ModifiedDate']
    ])

    filteredTickets = getFilteredTicketsBySelectedFilters(filteredTickets, [
      [selectedStatus, 'StatusId', false], // false means, not contain null filter
      [selectedPriorities, 'PriorityId', false],
      [selectedCategories, 'CategoryId', false],
      [selectedTicketTypes, 'TicketTypeId', false],
      [selectedAssignee, 'AssigneeId', true], // true means, contain null filter
      [selectedGroup, 'SupportGroupId', true],
      [selectedTeam, 'TeamId', true],
      [selectedRequester, 'RequesterId', true]
    ], currentUserId);

    filteredTickets = filterTicketsByTags(filteredTickets, selectedTags);
    filteredTickets = filterTicketsByLifecycle(filteredTickets, selectedlifeCycles);
    filteredTickets = filterTicketsByUserList(filteredTickets, [
      [selectedAffectedUser, 'AffectedUsers'],
      [selectedCollaborator, 'TicketCollaborators']
    ], currentUserId);
    filteredTickets = filterTicketsByCustomProperties(filteredTickets, filterState.selectedValues);
    filteredTickets = filterTicketsByScalarNumbers(filteredTickets, filterState.selectedValues);
  }

  return filteredTickets;
};

const filterByDate = (tickets: Ticket[], selectedValues: number[], field: string) => {
return [...(tickets?.filter((ticket: Ticket) =>
  (selectedValues?.includes(0) && IsDateLate(ticket[field])) ||
  (selectedValues?.includes(1) && IsDateToday(ticket[field])) ||
  (selectedValues?.includes(2) && IsDateTomorrow(ticket[field])) ||
  (selectedValues?.includes(3) && IsDateThisWeek(ticket[field])) ||
  (selectedValues?.includes(4) && IsDateNextWeek(ticket[field])) ||
  (selectedValues?.includes(5) && IsDateFuture(ticket[field])) ||
  (selectedValues?.includes(6) && ticket[field] === null)
) ?? [])];
}

const extractDate = (date: string) => {
  return new Date(new Date(date).toDateString())
}

const filterByDateValue = (tickets: Ticket[], selectedFilter: FilterAccordionValues[], field: string) => {
  const date = selectedFilter.length > 0 ? selectedFilter[0].date : null;
  const operator = selectedFilter.length > 0 ? selectedFilter[0].key : null;
  
  if (date == null) return [];
  switch (operator) {
    case 0:
        return [...(tickets?.filter((ticket: Ticket) => extractDate(ticket[field]) < extractDate(date)) ?? [])];
    case 1:
        return [...(tickets?.filter((ticket: Ticket) => extractDate(ticket[field]) <= extractDate(date)) ?? [])];
    case 2:
        return [...(tickets?.filter((ticket: Ticket) => extractDate(ticket[field]) > extractDate(date)) ?? [])];
    case 3:
        return [...(tickets?.filter((ticket: Ticket) => extractDate(ticket[field]) >= extractDate(date)) ?? [])];
  }
};

const paginatedTickets = (currentPage, ticketCountRef, filteredTickets, setTicketCount) => {
  if (ticketCountRef.current !== filteredTickets?.length) {
    setTicketCount(filteredTickets?.length);
  }
  ticketCountRef.current = filteredTickets?.length;

  const ITEM_PER_PAGE = TICKET_PAGINATION.ITEM_PER_PAGE();
  return filteredTickets.slice(currentPage * ITEM_PER_PAGE - ITEM_PER_PAGE, ITEM_PER_PAGE * currentPage);
};

export const filterListTickets = (state: AppStateValue, currentPage: number, setTicketCount: React.Dispatch<React.SetStateAction<number>>, ticketCountRef: React.MutableRefObject<number>) => {
  let currentUserId = state.currentUserId;
  let tickets: Ticket[], notClosedTickets = state.tickets;

  if (state.listFilter === TableFilterOptions.AllActive) {
    let filteredTickets = notClosedTickets;
    tickets = paginatedTickets(currentPage, ticketCountRef, filteredTickets, setTicketCount);
  } else if (state.listFilter === TableFilterOptions.MyWork) {
    let filteredTickets = notClosedTickets.filter((ticket: Ticket) => ticket.AssigneeId === currentUserId || ticket?.TicketCollaborators?.some((collaborator: any) => collaborator.PlatformUserId === currentUserId));
    tickets = paginatedTickets(currentPage, ticketCountRef, filteredTickets, setTicketCount);
  } else if (state.listFilter === TableFilterOptions.Unassigned) {
    let filteredTickets = notClosedTickets.filter((ticket: Ticket) => ticket.AssigneeId === null);
    tickets = paginatedTickets(currentPage, ticketCountRef, filteredTickets, setTicketCount);
  } else if (state.listFilter === TableFilterOptions.Closed) {
    const filteredTickets = state.closedTickets.filter((ticket: Ticket) => ticket.StatusId === getStatusIdByGuid(state.ticketStatus, STATUS_GUID.CLOSED));
    tickets = paginatedTickets(currentPage, ticketCountRef, filteredTickets, setTicketCount);
  } else if (state.listFilter === TableFilterOptions.Deflected) {
    const filteredTickets = state.closedTickets.filter((ticket: Ticket) => ticket.StatusId === getStatusIdByGuid(state.ticketStatus, STATUS_GUID.DEFLECTED));
    tickets = paginatedTickets(currentPage, ticketCountRef, filteredTickets, setTicketCount);
  }
  return customizedFilter(tickets, state.filterDropdown, state.filtersApplyCount, currentUserId);
};

export const Filter = (props: FilterProps) => {
  props.page = props.page ?? 'table';
  const setState = useSetState();
  const filterState = appState();
  const { listStates } = useContext(ListContext);
  const [visibleFilterIds, setVisibleFilterIds] = useState<number[]>(() => getStoredVisibleFilterIds());
  const [addFilterDropdownValue, setAddFilterDropdownValue] = useState<any>(null);
  const [addFilterSearchQuery, setAddFilterSearchQuery] = useState('');

  const isMounted = useRef<boolean>();
  const [clearNonce, setClearNonce] = useState<number>(0);
  const {t} = useTranslation();

  const getFilterCount = () => {
    let count = 0;
    filterState.filterDropdown.selectedValues.forEach((sv) => {
      if (sv.FilterDataType==FilterDataType.String){
        count = count + (sv.stringValue ? 1 : 0);
      }
      else {
        count = count + sv.values.filter((subfilter) => subfilter.selected).length;
      }
    });
    return !filterState.filterDropdown.queryValue ? count : count + 1;
  };

const actions = (action) => {
  if (action.event === 'setFilter') {
    const findFilterValue =
      filterState.filterDropdown.selectedValues.findIndex(
        (obj) => obj.id === action.data.id
      );
    let getCurrentValues = filterState.filterDropdown.selectedValues;
    if (findFilterValue >= 0) {
      let current = getCurrentValues[findFilterValue];
      let updateValues:FilterPropsItems;
      
      if (current?.FilterDataType==FilterDataType.String){
        updateValues = update(current, {
          values: { $set: action.data.values },
          stringValue: { $set: action.data.stringValue }
        });
      }
      else{
        updateValues = update(current, {
          values: { $set: action.data.values },
        });
      }
      
        let newData = update(getCurrentValues, {
          $splice: [[findFilterValue, 1, updateValues]],
        });
        getCurrentValues = newData;
      }

          setState(state => {
              return {
                  ...state,
                  filterDropdown: {
                      ...state.filterDropdown,
                      selectedValues: getCurrentValues
                  },
                  filterDropdownCount: getFilterCount()
              };
          });

          if (props.onChange) {
              props.onChange({
                  event: 'getData',
                  data: { filterValues: getCurrentValues }
              });
          }
      } else if (action.event === 'setAccordion') {
          let booleanToString = action.data.isCollapsed ? 'collapsed' : 'not';
          sessionStorage.setItem(`accordion-${action.data.sortOrder}`, booleanToString);
          // Matched on id, and written to a new array. With bare booleans in the list this
          // findIndex never matched, so `tempCollapseList[-1] = ...` set a stray property rather
          // than an element and the list never learned what had been opened - every later render
          // reset the panel to the state captured when it was first built.
          let tempCollapseList = [...filterState.filterDropdown.collapseList];
          const index = tempCollapseList.findIndex(x => x.id === action.data.id);
          const entry = { id: action.data.id, isCollapsed: action.data.isCollapsed };
          if (index >= 0) tempCollapseList[index] = entry;
          else tempCollapseList.push(entry);
          setState(state => {
              return {
                  ...state,
                  filterDropdown: {
                      ...state.filterDropdown,
                      collapseList: tempCollapseList,
                      filterDropdownCount: getFilterCount()
                  }
              };
          });
      }
  };

const queryChange = (query: string) => {
  if (query !== '') {
    setState((state) => {
      return {
        ...state,
        filterDropdown: {
          ...state.filterDropdown,
          isQueryEmpty: false,
        },
        filterDropdownCount: getFilterCount(),
      };
    });
  } else {
    setState((state) => {
      return {
        ...state,
        filterDropdown: {
          ...state.filterDropdown,
          isQueryEmpty: true,
        },
        filterDropdownCount: getFilterCount(),
      };
    });
  }
  if (props.onChange) {
    props.onChange({ event: 'queryChange', data: { query: query } });
  }
};

  const clearSelected = useCallback(() => {
      setState(state => {
          return {
              ...state,
              filterDropdown: {
                  ...state.filterDropdown,
                  isQueryEmpty: true,
                  queryValue: ''
              },
              filterDropdownCount: 0
          };
      });
      if (props.onChange) {
          props.onChange({ event: 'clearData' });
          setState(state => {
              return {
                  ...state,
                  filterDropdown: {
                      ...state.filterDropdown,
                      selectedValues: props.items
                  },
                  filterDropdownCount: getFilterCount()
              };
          });
      }

    applyFilters(props.defaultFilters, true);

    setClearNonce(n => n + 1);
  }, [setState, props.onChange, props.items, getFilterCount]);

  useEffect(() => {
  if (isMounted.current) clearSelected();
  isMounted.current = true;
}, []);

useEffect(() => {
      setState((state) => {
          return {
              ...state,
              filterDropdown: {
                  ...state.filterDropdown,
                  selectedValues: props.items,
              },
              filterDropdownCount: getFilterCount(),
          };
      });
      // Each entry carries the filter's id. It used to be a bare boolean, matched by position -
      // built here in props.items order and read back by the index of the SORTED list, which are
      // different orders for 20 of the 22 built-in filters. Measured 2026-09-07: expanding
      // Resolution Category, then reloading, opened Draft KB Article instead, because the entry
      // at sorted position 20 had been built from props.items[20] - Resolution Category.
      //
      // The toggle handler already looked entries up with `x.id`, so an id-keyed list is what the
      // rest of this file was written against; only the builder disagreed.
      let collapseOptions = [];
      props.items.forEach((item) => {
          let accordionCookie = sessionStorage.getItem(`accordion-${item.sortOrder}`);

          if (!accordionCookie) {
              accordionCookie = `collapsed`;
              sessionStorage.setItem(`accordion-${item.sortOrder}`, accordionCookie);
          }

          collapseOptions.push({ id: item.id, isCollapsed: accordionCookie === `collapsed` });
      });

      setState(state => {
          return {
              ...state,
              filterDropdown: {
                  ...state.filterDropdown,
                  collapseList: collapseOptions
              },
              filterDropdownCount: getFilterCount()
          };
      });

      const sortedItemIds = [...props.items]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((item) => item.id);

      setVisibleFilterIds((prev) => {
        const validPrevIds = prev.filter((id) => sortedItemIds.includes(id) || isCustomPropertyFilterId(id));
        if (validPrevIds.length > 0) {
          return validPrevIds;
        }

        const storedVisibleIds = getStoredVisibleFilterIds().filter((id) => sortedItemIds.includes(id) || isCustomPropertyFilterId(id));
        if (storedVisibleIds.length > 0) {
          return storedVisibleIds;
        }

        const defaultVisibleMainIds = [
                FILTERS.REQUESTERS.id,
                FILTERS.AffectedUsers.id,
                FILTERS.CATEGORY.id,
                FILTERS.TICKET_TYPE.id,
                FILTERS.STATUS.id,
                FILTERS.PRIORITY.id,
                FILTERS.TEAM.id,
                FILTERS.GROUP.id,
                FILTERS.ASSIGNEE.id
              ].filter((id): id is number => typeof id === 'number' && sortedItemIds.some((filter) => filter === id));

        return defaultVisibleMainIds.length > 0 ? defaultVisibleMainIds : sortedItemIds.slice(0, 13);
      });
  }, [props.items]);

  useEffect(() => {
    localStorage.setItem(TOOLBAR_ACTIVE_FILTERS_STORAGE_KEY, JSON.stringify(visibleFilterIds));
  }, [visibleFilterIds]);

  const applyFilters = async (dropdownValues: FilterPropsItems[], isClearSelected?: boolean) => {
    const filtersCount = isClearSelected ? 0 : getFilterCount();
    const filterInputValue = isClearSelected ? '' : filterState.filterDropdown?.queryValue;
    await setChildFiltersInStorage(
      dropdownValues,
      filtersCount,
      filterInputValue
    );
    
    setState((state) => ({
      ...state,
      filtersApplyCount: state.filtersApplyCount + 1,
      filterDropdown: {
        ...state.filterDropdown,
        queryValueApplied: state.filterDropdown?.queryValue
      }
    }));
  };

  const getLicenseFeatureName = (i: number, id?: number) => {
    // Matched on id, not position. The block below indexes into the sorted list, which is fine for
    // filters sitting inside the fixed built-in run; Total Agent Time is the last of them, so a
    // single filter added or removed above it would silently move it onto another feature's
    // licence. Gated on the same feature as the list column (ClassDetailList's useTimeTracking).
    if (id === FILTERS.TOTAL_AGENT_TIME.id) return LICENSE_FEATURE.TimeTracking;

    let featureName = LICENSE_FEATURE.ValidSubscription
    if(i === 5) featureName = LICENSE_FEATURE.MultiDepartment 
    else if (i === 7) featureName = LICENSE_FEATURE.TicketTypes
    else if (i === 8) featureName = LICENSE_FEATURE.TicketTags
    else if (i === 11) featureName = LICENSE_FEATURE.TicketLifecycle
    else if (i >= 17 && i <= 20) featureName = LICENSE_FEATURE.ResolutionNote

    return featureName
  }
  const checkIfDateExists = (id: number) => (filterState.filterDropdown.selectedValues.find(x => x.id === id)?.values.find(v => v.selected) ?? { date: new Date() }).date ? false : true;

  const addFilterToVisibleList = (filterId: number) => {
    setVisibleFilterIds((prev) => (prev.includes(filterId) ? prev : [...prev, filterId]));
    setAddFilterDropdownValue(null);
    setAddFilterSearchQuery('');
  };

  const removeFilterFromVisibleList = (filterId: number) => {
    setVisibleFilterIds((prev) => prev.filter((id) => id !== filterId));
  };
  
return <FluentUIThemeConsumer render={(globalTheme) => (
  <AppStateContext.Consumer>
    {(state) => {
      const filterDropdownValues = state.filterDropdown.selectedValues;
      const sortedFilterDropdownValues = [...filterDropdownValues].sort((a,b)=> a.sortOrder - b.sortOrder);
      const visibleFilterDropdownValues = visibleFilterIds
        .map((id) => sortedFilterDropdownValues.find((f) => f.id === id))
        .filter((f): f is FilterPropsItems => !!f);
      const addableFilterDropdownValues = sortedFilterDropdownValues.filter((f) => !visibleFilterIds.includes(f.id) && f.id !== FILTERS.Title.id);
      
      return (
        <Popup
          trapFocus
          trigger={
            <Dropdown
              items={[]}
              triggerButton={
                <span>{`${t('ticket.child-filter.title')} ${
                  getFilterCount() > 0 ? `(${getFilterCount()})` : ``
                }`}</span>
              }
              fluid
              className={`${AppCSS.dropdownItem} h-12 ${FilterCSS.hideItems} dropdown-btn`}
            />
          }
          position='below'
          align='end'
          onOpenChange={(e, t) => {
            const eventTarget = e?.target as HTMLElement | null;
            const isPeoplePickerInteraction = !!eventTarget?.closest('[class*="tikit-people-picker__"]');
            if (state.filterDropdown.isOpen && t?.open === false && isPeoplePickerInteraction) {
              return;
            }

            const nextOpenState = typeof t?.open === 'boolean' ? t.open : state.filterDropdown.isOpen;

            props.onChange({
              event: 'isOpen',
              data: { isOpen: nextOpenState },
            });
            setState((_state) => {
              return {
                ..._state,
                filterDropdown: {
                  ..._state.filterDropdown,
                  isOpen: nextOpenState,
                },
                filterDropdownCount: getFilterCount(),
              };
            });
          }}
          open={state.filterDropdown.isOpen}
          content={
            <div className={FilterCSS.filterPopup}>
              <div className={`p-2`}>
                <div className='flex justify-between items-center pb-2'>
                  <Text
                    content={t('ticket.child-filter.title')}
                    weight={'semibold'}
                    size={'large'}
                  />
                  <Text
                    content={t('ticket.child-filter.clear-btn')}
                    color='brand'
                    className={`cursor-pointer`}
                    onClick={clearSelected}
                  />
                </div>
                <Input
                  fluid
                  style={{
                    backgroundColor:
                      globalTheme.siteVariables.colorScheme.default
                        .background2,
                  }}
                  value={state.filterDropdown.queryValue}
                  onChange={(_e, p) => {
                    queryChange(p.value);
                    setState((_state) => {
                      return {
                        ..._state,
                        filterDropdown: {
                          ..._state.filterDropdown,
                          queryValue: p.value,
                        },
                        filterDropdownCount: getFilterCount()
                      };
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter"){
                      applyFilters(filterState.filterDropdown.selectedValues)
                    }
                  }}
                  inverted
                  placeholder={t('ticket.child-filter.filter-request')}
                />
              </div>
              <ul className={FilterCSS.filtersList}>
                {visibleFilterDropdownValues.map((f, i) => {
                  return (
                    <React.Fragment key={f.id}>
                      <CheckFeature featureName={getLicenseFeatureName(i, f.id)} key={f.id}>
                        <FilterAccordion
                          {...f}
                          clearNonce={clearNonce}
                          onInteraction={actions}
                          onRemoveFromView={removeFilterFromVisibleList}
                          selected={filterDropdownValues}
                          filterState={filterState}
                          // Defaults to collapsed. A filter with no entry - one added after the
                          // list was built - would otherwise get undefined, which reads as "not
                          // collapsed" and springs it open.
                          isCollapsed={state.filterDropdown.collapseList.find(x => x.id === f.id)?.isCollapsed ?? true}
                        />
                      </CheckFeature>
                    
                    </React.Fragment>
                )})}
              </ul>
              {addableFilterDropdownValues.length > 0 && (
                <div className='px-2 pt-2'>
                  <Dropdown
                    style={{
                      backgroundColor:
                        globalTheme.siteVariables.colorScheme.default
                          .background2,
                    }}
                    fluid
                    search
                    clearable={addFilterDropdownValue != null}
                    value={addFilterDropdownValue}
                    searchQuery={addFilterSearchQuery}
                    items={addableFilterDropdownValues.map((f) => ({
                      key: `add-filter-${f.id}`,
                      header: f.title,
                      value: f.id,
                    }))}
                    placeholder='Add filter...'
                    onSearchQueryChange={(_e, data) => setAddFilterSearchQuery(String(data?.searchQuery || ''))}
                    onChange={(_e, data) => {
                      setAddFilterDropdownValue(data?.value);
                      if(data?.value === null) return;
                      const filterId = Number((data?.value as any)?.value ?? data?.value);
                      if (!Number.isNaN(filterId)) {
                        addFilterToVisibleList(filterId);
                      }
                    }}
                  />
                </div>
              )}
              <div className='flex justify-center'>
                <Button
                  content={t('ticket.child-filter.apply-filters')}
                  primary
                  className='mt-2 mb-2 w-full'
                  onClick={() => applyFilters(filterState.filterDropdown.selectedValues)}
                  disabled={(props.page === 'board' ? false : listStates.isTicketsFetching) || checkIfDateExists(FILTERS.CREATED_DATE.id) || checkIfDateExists(FILTERS.MODIFIED_DATE.id)}
                />
              </div>
              </div>
            }
          />
      );
    }}
  </AppStateContext.Consumer>
 
)} 
/>
};


export const clearFilterStates = (
  setState: React.Dispatch<React.SetStateAction<AppStateValue>>,
  defaultFilters: FilterPropsItems[]
) => {
  setState((prev) => ({
    ...prev,
    filterDropdown: {
      ...prev.filterDropdown,
      isQueryEmpty: true,
      queryValue: '',
      selectedValues: defaultFilters,
    },
    filterDropdownCount: 0,
  }));
};
